import type { TimestampLike } from '../types/common';

/**
 * Format a timestamp as a localized time string (e.g., "3:00 PM").
 * Uses the event's IANA timezone for correct local display.
 */
export function formatTime(timestamp: TimestampLike, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp.toDate());
}

/**
 * Format a time range (e.g., "3:00 – 4:30 PM").
 * Omits the start period if both are the same (e.g., "3:00 – 4:30 PM" not "3:00 PM – 4:30 PM").
 */
export function formatTimeRange(
  start: TimestampLike,
  end: TimestampLike,
  timezone: string,
): string {
  const startDate = start.toDate();
  const endDate = end.toDate();

  const opts: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
  };

  const endStr = new Intl.DateTimeFormat('en-US', opts).format(endDate);

  // Check if both times share the same period (AM/PM)
  const startParts = new Intl.DateTimeFormat('en-US', opts).formatToParts(startDate);
  const endParts = new Intl.DateTimeFormat('en-US', opts).formatToParts(endDate);
  const startPeriod = startParts.find((p) => p.type === 'dayPeriod')?.value;
  const endPeriod = endParts.find((p) => p.type === 'dayPeriod')?.value;

  if (startPeriod === endPeriod) {
    // Strip the period from start: "3:00 – 4:30 PM"
    const startStr = startParts
      .filter((p) => p.type !== 'dayPeriod' && p.type !== 'literal' || p.type === 'literal' && p.value === ':')
      .map((p) => p.value)
      .join('')
      .trim();
    return `${startStr} – ${endStr}`;
  }

  const startStr = new Intl.DateTimeFormat('en-US', opts).format(startDate);
  return `${startStr} – ${endStr}`;
}

/**
 * Format a DateString ("YYYY-MM-DD") as a readable date (e.g., "Wed, Apr 15").
 * No timezone needed — DateString is already localized by Cloud Functions.
 */
export function formatDateShort(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a DateString ("YYYY-MM-DD") as a full date (e.g., "Wednesday, April 15, 2026").
 */
export function formatDateFull(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Format a DateString as a medium-length date (e.g., "Jun 15, 2026").
 */
export function formatDateMedium(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Calendar-day difference between two DateStrings (`toDate - fromDate`).
 * Exact: both endpoints are anchored at UTC midnight, so no DST wobble
 * can leak in. Used by the duplicateEvent callable's schedule shift and
 * the admin dialog's end-date preview — one implementation, both sides.
 */
export function diffCalendarDays(fromDate: string, toDate: string): number {
  const [fy, fm, fd] = fromDate.split('-').map(Number);
  const [ty, tm, td] = toDate.split('-').map(Number);
  return (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000;
}

/**
 * Add (or subtract) whole calendar days to a DateString. Month/year
 * overflow handled by Date.UTC.
 */
export function addCalendarDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * Format a DateString range as a readable span. Collapses to a single month
 * when start and end share a month/year (e.g., "Jun 15–17, 2026"), falls
 * back to two full dates otherwise ("Jun 30 – Jul 2, 2026").
 */
export function formatDateRange(startDate: string, endDate: string): string {
  if (!startDate) return '';
  if (!endDate || startDate === endDate) return formatDateMedium(startDate);
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  if (sy === ey && sm === em) {
    const monthName = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
      new Date(sy, sm - 1),
    );
    return `${monthName} ${sd}\u2013${ed}, ${sy}`;
  }
  return `${formatDateMedium(startDate)} \u2013 ${formatDateMedium(endDate)}`;
}
